import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  ApiError,
  AVATAR_UPLOAD_MAX_BYTES,
  avatarUploadContentTypeSchema,
  avatarUploadPresignSchema,
  ownProfileSchema,
  publicProfileSchema,
  sessionProfileSchema,
  type AccountLifecycleBody,
  type AvatarPatch,
  type AvatarUploadPresign,
  type AvatarUploadPresignBody,
  type BioPatch,
  type DisplayNamePatch,
  type OwnProfile,
  type PublicProfile,
  type SessionProfile,
} from '@molt/shared';
import { emptyResponseSchema, get, patch, post, request } from './api';
import { formatAttachmentSize } from './attachments';
import { queryKeys } from './query-keys';

/**
 * The profile endpoints and their mutations — chunk `D1`, agent E (`FE-06.2`).
 *
 * `lib/cards.ts` and `lib/lists.ts` are the house pattern this file follows:
 * endpoint functions spelling their own path on top of `./api`'s verbs, then
 * hooks. It diverges from them in exactly the ways the brief calls for
 * (§5.5, §6.5) and is worth saying once, here, rather than at each hook:
 *
 *  - **No optimistic write.** `lib/optimistic.ts` exists and this module is
 *    deliberately not a caller of it: none of the three patches computes a
 *    rank or opens a gap, and the round trip sits behind a Save button that
 *    is already showing `busy` — there is nothing an optimistic write would
 *    make feel faster. C3's finding 5 counted six optimistic-id gates as an
 *    accumulating cost; this file adds none.
 *  - **`onSuccess` writes the response, it never invalidates.** Every patch
 *    route returns the *complete* `ownProfileSchema`, with `initials`
 *    re-derived server-side from the new display name. An invalidation would
 *    be a second request for a row we were just handed, and would leave the
 *    account menu's avatar briefly out of sync with the field the member just
 *    saved. `queryClient.setQueryData(queryKeys.me(), response)` is the whole
 *    of it, and is what makes a rename appear in the header without a reload
 *    — `FR-PERM-002` AC2.
 *
 * ---------------------------------------------------------------------------
 * THREE SPELLINGS, AND THIS FILE USES THE MIDDLE ONE
 * ---------------------------------------------------------------------------
 * `get`/`patch` from `./api` prepend `BASE = '/api'` inside `request()`, so
 * every path below is the **server's** spelling — `/me`, `/users/:id`,
 * `/me/display-name` — never the browser's `/api/...` one. Passing `/api/me`
 * here would ask the server for `/api/api/me` and 404.
 *
 * ⚠ **Clearing a bio or an avatar is `null`, never `''`.** `bioSchema` is
 * `.trim().min(1)` and 422s the empty string on the way in — one state, one
 * representation, per `packages/shared/src/user.ts`'s own argument. This
 * file does not re-validate that; it trusts `DisplayNamePatch`/`BioPatch`/
 * `AvatarPatch` to already be shaped that way, and `ProfilePanel` (agent F)
 * is the caller that turns an emptied field into `null` before it reaches
 * these functions.
 */

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * `GET /me` — the signed-in account's own view. 401 with no session.
 *
 * ⚠ `sessionProfileSchema`, never `ownProfileSchema` (SA-9). The owner view is
 * a plain `z.object`, so parsing with it strips `isSuper` rather than refusing
 * it, and every client gate would read `false` for a real superadmin with
 * nothing red anywhere.
 */
export function getMe(): Promise<SessionProfile> {
  return get('/me', sessionProfileSchema);
}

/**
 * `GET /users/:id` — another member's public profile.
 *
 * ⚠ **The one speculative export in this chunk.** It has no caller today:
 * D1 ships only the member's own profile at `/profile` (brief §2.2 — the
 * public profile page is `FE-44.2`, Phase 2, and B06's own instruction is to
 * decide that boundary when B44 is reached, not here). This exists now,
 * tested, so `FE-44.2` finds one spelling of the route and one key
 * (`queryKeys.users.detail`) rather than inventing a second — the exact
 * failure mode `query-keys.ts`'s own docstring is written to prevent. An
 * untested unused export would be dead code; this one ships with its test
 * and this docstring naming its first caller, which is what makes it a seam
 * instead.
 */
export function getUser(userId: string): Promise<PublicProfile> {
  return get(`/users/${encodeURIComponent(userId)}`, publicProfileSchema);
}

/** `PATCH /me/display-name`. Trimmed 1–128 server-side; `displayNameSchema`. */
export function patchDisplayName(body: DisplayNamePatch): Promise<OwnProfile> {
  return patch('/me/display-name', ownProfileSchema, body);
}

/** `PATCH /me/bio`. `body.bio` is `null` to clear — never `''` (see header). */
export function patchBio(body: BioPatch): Promise<OwnProfile> {
  return patch('/me/bio', ownProfileSchema, body);
}

/** `PATCH /me/avatar`. `body.avatarUrl` is `null` to remove — never `''`. */
export function patchAvatar(body: AvatarPatch): Promise<OwnProfile> {
  return patch('/me/avatar', ownProfileSchema, body);
}

// ---------------------------------------------------------------------------
// The avatar UPLOAD — `FE-44.2`, `FR-NOTIF-033` AC1
// ---------------------------------------------------------------------------
//
// ⚠⚠ THREE CALLS, AND THE MIDDLE ONE DELIBERATELY DOES NOT USE `./api`.
//
// `BE-44.3` shipped `POST /me/avatar/presign` and `GET /users/:id/avatar/:id`
// months before anything called them: the presign had **no caller anywhere in
// the product**, so what a member actually saw was a box asking them to paste a
// URL they had no way to obtain. These functions are that caller.
//
//   1. `POST /me/avatar/presign` → a signed PUT **and** the durable URL;
//   2. `PUT` those bytes straight to object storage — SC-3, they never pass
//      through the API process;
//   3. the shipped `PATCH /me/avatar` with the URL step 1 returned.
//
// There is no confirm route and none is missing: `users.avatar_url` *is* the
// record, and `avatarUrlSchema`'s http/https restriction — the thing that stops
// a member storing `javascript:` and having it rendered as an `<img src>` to
// everyone who shares a board with them — still guards step 3.
//   `apps/server/src/routes/profile-avatar.ts` carries the full argument.

/**
 * Mirrors `AttachmentTranslate` in `./attachments.ts` and `CommentTranslate` in
 * `./comments.ts`, for the same reason all three exist: **a non-component
 * module must never call `useInterfaceLanguage()`.** With no provider mounted
 * the hook resolves against `fallbackCatalogue` and renders English
 * *successfully*, forever — a defect with nothing red anywhere. Taking the
 * translator as a required first parameter makes `tsc` enumerate every call
 * site instead.
 */
export type ProfileTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

/**
 * `POST /me/avatar/presign`.
 *
 * The body is exactly the two facts a browser knows about a chosen `File`, and
 * the contract already had a name for both — nothing in `packages/shared` was
 * widened to wire this up.
 */
export function presignAvatarUpload(
  body: AvatarUploadPresignBody,
): Promise<AvatarUploadPresign> {
  return post('/me/avatar/presign', avatarUploadPresignSchema, body);
}

/**
 * Step 2: the bytes, straight to object storage.
 *
 * ⚠⚠ **This is a bare `fetch` and it must stay one.** Every other request in
 * this module goes through `./api`, which prepends `/api`, pins
 * `credentials: 'same-origin'` so no call site can drop the session cookie, and
 * parses the reply with a Zod schema. All three are *wrong* here, and the
 * middle one is the one that matters: `uploadUrl` names the object store, a
 * different origin, and sending our session cookie to it would be a leak with
 * no upside whatsoever. `putAttachmentBytes` in `./attachments.ts` is the house
 * precedent this follows line for line.
 *
 * ⚠ **`presign.headers` is passed through untouched.** The presigned URL is
 * signed *over* those headers — `content-type` and `content-length` — so
 * omitting or altering either produces a signature error rather than a helpful
 * one. That signed `content-length` is also the only thing enforcing
 * `AVATAR_UPLOAD_MAX_BYTES` against a client that lies about the size: the
 * browser check below is a courtesy, the signature is the cap.
 */
export async function putAvatarBytes(
  t: ProfileTranslate,
  presign: AvatarUploadPresign,
  file: Blob,
): Promise<void> {
  let response: Response;

  try {
    response = await fetch(presign.uploadUrl, {
      method: 'PUT',
      headers: presign.headers,
      body: file,
    });
  } catch (cause) {
    throw new ApiError('internal', t('profile.avatarUpload.bytesFailed'), {
      network: [cause instanceof Error ? cause.message : String(cause)],
    });
  }

  if (!response.ok) {
    throw new ApiError(
      'internal',
      t('profile.avatarUpload.refused', { status: response.status }),
    );
  }
}

/**
 * Why the caller may not upload this file, or `null` if it may.
 *
 * ⚠ **The server is authoritative and this function is not a second gate.**
 * `POST /me/avatar/presign` answers **422** to an unsupported content type, to
 * a `sizeBytes` above `AVATAR_UPLOAD_MAX_BYTES` and to an unknown key, and
 * `apps/server/src/routes/profile-avatar.integration.test.ts` asserts all three
 * — a client that skips this check gets a refusal, never an upload. What this
 * buys is a *sentence a member can act on* instead of a validation error naming
 * a wire field, and it buys it before a single byte leaves the machine.
 *
 * It lives here rather than in `routes/profile.tsx` because that route has no
 * rendering test — `routes/profile.test.tsx` reads its source — so logic put
 * there would be logic nothing could exercise.
 *
 * ⚠ **Type first, then size, and `file.type` trusted as given — the same gate
 * `BoardBackgroundPicker.chooseFile` makes, deliberately.** That component is
 * this one's structural twin (a restricted content-type enum, a presign, a
 * direct PUT) and it tests `BOARD_BACKGROUND_UPLOAD_CONTENT_TYPES.includes(
 * file.type)` before its size check. `CardAttachmentSection` does the opposite
 * — `file.type || 'application/octet-stream'` — and that is not a competing
 * precedent: **its** route accepts any content type, so it has nothing to test
 * `file.type` against and every reason not to send an empty one.
 *
 * The cost of trusting `file.type` is real and is stated rather than hidden: a
 * browser that reports `''` for a legitimate PNG refuses a member an upload
 * they should have had. It is the empty string the presign body has no
 * representation for either, so forwarding it buys a 422 naming a wire field
 * instead of a sentence — and whatever the right answer is, it is the same
 * answer for both pickers and not one this module may give alone.
 */
export function avatarFileRejection(t: ProfileTranslate, file: File): string | null {
  if (!avatarUploadContentTypeSchema.safeParse(file.type).success) {
    return t('profile.avatarUpload.rejectedType');
  }

  if (file.size > AVATAR_UPLOAD_MAX_BYTES) {
    return t('profile.avatarUpload.rejectedSize', {
      size: formatAttachmentSize(t, AVATAR_UPLOAD_MAX_BYTES),
    });
  }

  return null;
}

/**
 * The whole flow, as one promise: presign, PUT, patch.
 *
 * ⚠ **`file.type` is PARSED here rather than cast, and the difference matters.**
 * An earlier draft narrowed it with `as AvatarUploadContentType` on the
 * reasoning that `avatarFileRejection` has already run — which is true of the
 * route that calls this today and is an invariant **nothing enforces**. This
 * function is exported, `useUploadAvatar` hands it whatever `File` it is given,
 * and a caller that skipped the gate would have posted an arbitrary string as
 * `contentType` and collected a 422 naming a wire field: exactly the outcome
 * the early refusal exists to prevent. Parsing costs one line and makes the
 * failure the same sentence a member would have seen a moment earlier.
 */
export async function uploadAvatar(t: ProfileTranslate, file: File): Promise<OwnProfile> {
  const contentType = avatarUploadContentTypeSchema.safeParse(file.type);
  if (!contentType.success) {
    throw new ApiError('validation_failed', t('profile.avatarUpload.rejectedType'));
  }

  const presign = await presignAvatarUpload({
    contentType: contentType.data,
    sizeBytes: file.size,
  });
  await putAvatarBytes(t, presign, file);
  return patchAvatar({ avatarUrl: presign.avatarUrl });
}

/** `POST /account/deactivate` — soft closure that keeps authored content. */
export function deactivateAccount(): Promise<null> {
  const body: AccountLifecycleBody = { confirm: true };
  return post('/account/deactivate', emptyResponseSchema, body);
}

/**
 * `DELETE /account` — 204 with a confirmation body.
 *
 * `del()` is bodyless, so this route goes through `request()` like workspace
 * deletion.
 */
export function deleteAccount(): Promise<null> {
  const body: AccountLifecycleBody = { confirm: true };
  return request('/account', emptyResponseSchema, {
    method: 'DELETE',
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * The one shape all three mutations share: call the given endpoint function,
 * and on success write its response into `['me']` (brief §5.5). Factored out
 * so the three hooks below cannot drift into three different `onSuccess`
 * bodies — the same argument `lib/lists.ts`'s `announceRollback` makes for
 * putting a repeated behaviour in one function rather than three call sites.
 *
 * No `onError` here: a failed patch has nothing to roll back (there was no
 * optimistic write) and nothing to announce from this layer — `ProfilePanel`
 * is presentational and takes `error` as a prop (brief §6.6), and the route
 * that wires it is what turns a thrown `ApiError` into that prop.
 */
function useProfilePatch<TVariables>(
  mutationFn: (body: TVariables) => Promise<OwnProfile>,
): UseMutationResult<OwnProfile, ApiError, TVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: (profile) => {
      /*
       * ⚠ MERGED, NOT WRITTEN VERBATIM (SA-9). The patch routes serialise
       * `ownProfileSchema`, which carries no `isSuper`, so the response alone
       * would erase the flag `GET /me` put in this cache — renaming yourself
       * would hide the admin console until the next probe. With nothing
       * cached there is no flag to keep, and the response is not entitled to
       * invent one, so an `undefined` return leaves the cache untouched and
       * the session probe fills it.
       */
      queryClient.setQueryData<SessionProfile>(queryKeys.me(), (prev) =>
        prev === undefined ? prev : { ...profile, isSuper: prev.isSuper },
      );
    },
  });
}

export function useUpdateDisplayName(): UseMutationResult<
  OwnProfile,
  ApiError,
  DisplayNamePatch
> {
  return useProfilePatch(patchDisplayName);
}

export function useUpdateBio(): UseMutationResult<OwnProfile, ApiError, BioPatch> {
  return useProfilePatch(patchBio);
}

export function useUpdateAvatar(): UseMutationResult<OwnProfile, ApiError, AvatarPatch> {
  return useProfilePatch(patchAvatar);
}

/**
 * Presign → PUT → `PATCH /me/avatar`, behind one `isPending` and one `error`.
 *
 * ⚠ **`t` is a parameter rather than a `useInterfaceLanguage()` call inside the
 * hook**, matching `putAvatarBytes` and `formatAttachmentSize`. A hook could
 * legally read the context — it only ever runs under a mounted provider — but
 * splitting the rule by call shape is how a module ends up with two ways to
 * reach the catalogue, and the failure mode of the wrong one (English, rendered
 * successfully, forever) is invisible. One way, everywhere in this module.
 *
 * It reuses `useProfilePatch` deliberately: the three shipped patches and this
 * upload all end at the same place — a complete `ownProfileSchema` written into
 * `['me']`, never an invalidation. That single `onSuccess` is what makes the
 * new avatar appear in the shell's account menu without a reload, and routing
 * the upload through a second, hand-written `onSuccess` is exactly how the two
 * would drift.
 */
export function useUploadAvatar(
  t: ProfileTranslate,
): UseMutationResult<OwnProfile, ApiError, File> {
  return useProfilePatch((file: File) => uploadAvatar(t, file));
}

function useAccountLifecycle(
  mutationFn: () => Promise<null>,
): UseMutationResult<null, ApiError, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

export function useDeactivateAccount(): UseMutationResult<null, ApiError, void> {
  return useAccountLifecycle(deactivateAccount);
}

export function useDeleteAccount(): UseMutationResult<null, ApiError, void> {
  return useAccountLifecycle(deleteAccount);
}
